import './Filter.scss';

import { FadersHorizontal } from '@phosphor-icons/react';
import { createSearchParams, useSearchParams } from 'react-router';
import { ButtonV2, Checkbox } from '@travelwin/core';
import { useFormik } from 'formik';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Sort from '../../components/Sort/Sort';
import { testUserAgentForMobileWeb } from '../../hooks/useDeviceOS';
import FilterOptionsList from './FilterOptionsList/FilterOptionsList';

const className = 'p-Filter';

type FilterProps = {
  categories: Record<string, string[]>;
  displayNames: Record<string, string>;
  children: ReactNode;
  onSortChange: (sort: string) => void;
  userAgent: string;
};

type FilterFormValues = {
  query: string;
  categories: Record<string, string[]>;
};

export const Filter = ({
  categories,
  displayNames,
  children,
  onSortChange,
  userAgent,
}: FilterProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCategories = Object.keys(categories).reduce(
    (acc, key) => {
      acc[key] = searchParams.getAll(key);
      return acc;
    },
    {} as Record<string, string[]>,
  );

  const [showFilters, setShowFilters] = useState(() => {
    const initialCategoriesCount = Object.values(initialCategories).reduce(
      (acc, curr) => acc + curr.length,
      0,
    );
    return initialCategoriesCount > 0 && !testUserAgentForMobileWeb(userAgent);
  });

  const formik = useFormik<FilterFormValues>({
    initialValues: {
      query: searchParams.get('q') ?? '',
      categories: initialCategories,
    },
    onSubmit: (values) => {
      setSearchParams(getSearchParamsFromValues(values), {
        replace: true,
        preventScrollReset: true,
      });
    },
  });

  const handleSortChange = (sort: string) => {
    onSortChange(sort);
  };

  const handleClear = () => {
    const values = {
      query: '',
      categories: Object.keys(categories).reduce(
        (acc, key) => {
          acc[key] = [];
          return acc;
        },
        {} as Record<string, string[]>,
      ),
    };

    formik.resetForm({
      values,
    });
    setSearchParams(getSearchParamsFromValues(values), {
      replace: true,
      preventScrollReset: true,
    });
  };

  const isFilterActive = () => {
    return (
      formik.values.query.length > 0 ||
      Object.values(formik.values.categories).some(
        (category) => category.length > 0,
      )
    );
  };

  const filtersCount = Object.values(formik.values.categories).reduce(
    (acc, curr) => {
      return acc + curr.length;
    },
    0,
  );

  return (
    <form className={className} onSubmit={formik.handleSubmit}>
      <div className={`${className}__buttons-container`}>
        <div className={`${className}__filterButton`}>
          <ButtonV2
            size={'small'}
            onClick={() => setShowFilters(!showFilters)}
            variant={'secondary'}
            icons={{ leading: <FadersHorizontal size={24} /> }}
          >
            <div className={`${className}__filterButton--mobile`}>
              {showFilters
                ? t('filter.hideFilters', 'Hide Filters')
                : t('filter.filters', 'Filters')}
              {filtersCount ? ` (${filtersCount})` : null}
            </div>
            <div className={`${className}__filterButton--desktop`}>
              {showFilters
                ? t('filter.hideFilters', 'Hide Filters')
                : t('filter.showFilters', 'Show Filters')}
              {filtersCount ? ` (${filtersCount})` : null}
            </div>
          </ButtonV2>
        </div>
        <Sort onSortChange={handleSortChange} />
      </div>
      <div className={`${className}__categories-container`}>
        {Object.keys(categories).length > 0 && showFilters && (
          <FilterOptionsList
            showClearButton={isFilterActive()}
            onClear={handleClear}
            onClose={() => setShowFilters(false)}
          >
            {Object.keys(categories).map((categoryKey) => (
              <div key={categoryKey} className={`${className}__category`}>
                <div className={`${className}__category-title`}>
                  {categoryKey}
                </div>
                {categories[categoryKey].map((option) => (
                  <Checkbox
                    key={option}
                    id={`category-${categoryKey}-${option}`}
                    name={`categories.${categoryKey}`}
                    value={option}
                    checked={formik.values.categories[categoryKey]?.includes(
                      option,
                    )}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.submitForm();
                    }}
                  >
                    {displayNames[option] || option}
                  </Checkbox>
                ))}
              </div>
            ))}
          </FilterOptionsList>
        )}
        {children}
      </div>
    </form>
  );
};

const getSearchParamsFromValues = (values: FilterFormValues) => {
  const params: Record<string, string | string[]> = {
    q: values.query.length > 0 ? values.query : [],
  };

  Object.keys(values.categories).forEach((key) => {
    params[key] = values.categories[key];
  });

  return createSearchParams(params);
};

export default Filter;
